/* FILE: ./frontend/src/features/auth/LoginForm.jsx */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next'; 
import { supabase } from '../../lib/supabase';

const LoginForm = () => {
  const { t } = useTranslation(); 
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [loginMethod, setLoginMethod] = useState('otp'); // 'otp' | 'password'
  const [loginStep, setLoginStep] = useState('email'); // 'email' | 'otp'
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const { signIn, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ⚡ DEEP LINK FIX: Read from session storage if state is empty
  const returnTo = location.state?.returnTo || location.state?.from?.pathname || sessionStorage.getItem('returnTo'); 

  // ⚡ Save it to session storage the moment they arrive
  useEffect(() => {
    if (returnTo) {
        sessionStorage.setItem('returnTo', returnTo);
    }
  }, [returnTo]);

  // --- 1. BOUNCER: If you visit this page while logged in, kick you out ---
  useEffect(() => {
    if (user) {
      const from = returnTo || '/directory';
      sessionStorage.removeItem('returnTo'); // ⚡ Clean up before leaving
      navigate(from, { replace: true });
    }
  }, [user, navigate, returnTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      // --- 0. B2B BUSINESS EMAIL FILTER (Block Gmail, Yahoo, Hotmail, etc.) ---
      const blockedDomains = [
        'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 
        'aol.com', 'icloud.com', 'protonmail.com', 'zoho.com', 
        'yandex.com', 'mail.com', 'msn.com', 'live.com'
      ];
      const emailDomain = email.split('@')[1]?.toLowerCase();
      if (blockedDomains.includes(emailDomain)) {
        throw new Error(t('auth.err_business_email_required', 'Please use your corporate email address to access the B2B network.'));
      }

      if (loginMethod === 'password') {
        // --- LEGACY PASSWORD LOGIN ATTEMPT ---
        const { data, error: signInError } = await signIn(email, password);
        if (signInError) {
          setError(signInError.message || t('login.failed_cred'));
          return; 
        } 
        
        if (data) {
          const from = returnTo || '/directory';
          sessionStorage.removeItem('returnTo'); // ⚡ Clean up before leaving
          navigate(from, { replace: true });
        }
      } else {
        // --- PASSWORDLESS OTP LOGIN FLOW ---
        if (loginStep === 'email') {
          // ⚡ shouldCreateUser: false ensures unregistered emails cannot trigger a code from login
          const { error: otpError } = await supabase.auth.signInWithOtp({ 
            email,
            options: { shouldCreateUser: false } 
          });

          if (otpError) {
            // Check if error is due to user not existing
            if (otpError.status === 422 || otpError.message?.toLowerCase().includes('signup') || otpError.message?.toLowerCase().includes('not found')) {
              throw new Error('No account found with this email address. Please create a free account first.');
            }
            throw otpError;
          }

          setLoginStep('otp');
        } else if (loginStep === 'otp') {
          const { data: { session }, error: verifyError } = await supabase.auth.verifyOtp({ 
            email, 
            token: otp, 
            type: 'email' 
          });
          if (verifyError) throw verifyError;
          
          if (session) {
            const from = returnTo || '/directory';
            sessionStorage.removeItem('returnTo'); // ⚡ Clean up before leaving
            navigate(from, { replace: true });
          }
        }
      }
    } catch (err) {
      setError(err.message || t('login.unexpected'));
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/update-password`,
      });

      if (error) throw error;

      alert(t('login.reset_alert', { email: resetEmail }));
      setShowForgotPassword(false);
      setResetEmail('');

    } catch (err) {
      console.error("Reset Password Error:", err.message);
      setError(err.message || "Failed to send reset email.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- FORGOT PASSWORD VIEW ---
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-lg">
          <div className="flex justify-center">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-lg">TB</span>
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('login.reset_title')}
          </h2>
          <p className="mt-3 text-center text-base text-gray-600">
            {t('login.reset_desc')}
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
          <div className="bg-white py-10 px-8 shadow-xl rounded-2xl sm:px-12 border border-gray-100">
            <form className="space-y-6" onSubmit={handleForgotPassword}>
              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('login.email_label')}
                </label>
                <input
                  id="reset-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-base" 
                  placeholder={t('login.email_ph')}
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              {error && (
                <div className="text-sm text-red-600 text-center">
                  {error}
                </div>
              )}

              <div className="flex space-x-4 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  disabled={isLoading}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {t('login.back_to_login')}
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-900 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex justify-center items-center"
                >
                  {isLoading ? (
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    t('login.send_reset')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // --- MAIN LOGIN VIEW ---
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-lg">
        <div className="flex justify-center">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-md">
              <span className="text-white font-bold text-lg">TB</span>
            </div>
            <span className="text-3xl font-extrabold text-gray-900 tracking-tight">TariffBeater</span>
          </Link>
        </div>
        <h2 className="mt-8 text-center text-3xl font-bold text-gray-900">
          {loginStep === 'otp' ? 'Enter Verification Code' : t('login.signin_title')}
        </h2>
        <p className="mt-3 text-center text-base text-gray-600">
          {t('login.or')}{' '}
          <Link
            to="/register"
            state={{ returnTo }} 
            className="font-medium text-blue-600 hover:text-blue-500 underline decoration-2 decoration-blue-100 hover:decoration-blue-500 transition-all"
          >
            {t('login.create_new')}
          </Link>
        </p>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-lg">
        <div className="bg-white py-10 px-8 shadow-xl rounded-2xl sm:px-12 border border-gray-100">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{t('login.login_failed')}</h3>
                    <div className="mt-1 text-sm text-red-700">
                      <p>{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {loginStep === 'email' && (
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('login.email_label')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-shadow"
                  placeholder={t('login.email_ph')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
              </div>
            )}

            {loginMethod === 'password' && loginStep === 'email' && (
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('login.password_label')}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-base transition-shadow"
                  placeholder={t('login.password_ph')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                />
              </div>
            )}

            {loginStep === 'otp' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  6-Digit Verification Code
                </label>
                <input
                  type="text"
                  required
                  maxLength="6"
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-center text-xl tracking-widest font-semibold"
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
                  disabled={isLoading}
                />
                <p className="text-xs text-gray-500 mt-2 text-center">We sent a verification code to {email}</p>
              </div>
            )}

            {loginMethod === 'password' && loginStep === 'email' && (
              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isLoading}
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                    {t('login.remember')}
                  </label>
                </div>

                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm font-medium text-blue-600 hover:text-blue-500"
                  disabled={isLoading}
                >
                  {t('login.forgot')}
                </button>
              </div>
            )}

            {loginStep === 'email' && (
              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setLoginMethod(prev => prev === 'otp' ? 'password' : 'otp');
                    setError(null);
                  }}
                  className="text-sm font-medium text-blue-600 hover:text-blue-500 underline"
                >
                  {loginMethod === 'otp' ? 'Log in with a password instead' : 'Use secure email code instead'}
                </button>
              </div>
            )}

            <div className="pt-2">
              <button
                type="submit"
                disabled={isLoading || (loginStep === 'otp' && otp.length !== 6)}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-blue-900 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{t('login.signing_in', 'Processing...')}</span>
                  </>
                ) : (
                  <span>
                    {loginMethod === 'password' 
                      ? t('login.sign_in_btn', 'Sign in') 
                      : (loginStep === 'email' ? 'Send 6-Digit Code' : 'Verify & Sign In')}
                  </span>
                )}
              </button>
            </div>

            {/* ⚡ NEW: FREEMIUM COPY CALLOUT BOX */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100 text-center shadow-inner">
                <h3 className="text-lg font-black text-blue-900 mb-2 tracking-tight">
                  {t('login.new_to', 'New to TariffBeater?')}
                </h3>
                <p className="text-sm text-blue-800 mb-6 font-medium leading-relaxed">
                  Join the Canadian Core & Shell network. Gain free access to trade simulations, compliance checks, and the national directory.
                </p>
                <Link
                  to="/register"
                  state={{ returnTo }} 
                  className="w-full flex justify-center py-3.5 px-4 border-2 border-blue-600 rounded-lg shadow-sm text-base font-bold text-blue-700 bg-white hover:bg-blue-600 hover:text-white transition-all transform hover:-translate-y-0.5"
                >
                  {t('login.create_acct_btn', 'Create your free account')}
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;